/* eslint-disable react/no-unescaped-entities */
"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useApi } from "@/context/ApiContext";
import { getImageURL } from "@/utils/cdn";
import type { ActiveShowSummary } from "@/lib/api/types";

export default function PortalHome() {
  const apiClient = useApi();
  const [shows, setShows] = useState<ActiveShowSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    apiClient.user
      .getActiveShows()
      .then((resp) => {
        if (!mounted) return;
        setShows(resp.shows || []);
      })
      .catch((e) => {
        console.error("Failed to load active shows:", e);
        if (mounted) setError("Failed to load shows");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [apiClient]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-gray-200">Loading…</div>;
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full bg-[#f5f7fb] text-neutral-900 font-['Helvetica_Now',_Arial,_sans-serif]">
      {/* Header */}
      <div className="mx-auto w-full max-w-7xl px-6 pt-12 pb-6 text-center">
        <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">Choose a show</h1>
        <p className="mt-3 text-sm text-gray-600 md:text-base">Tap a card to go to that show's submission page</p>
      </div>

      {/* Cards Grid */}
      <div className="mx-auto w-full max-w-7xl px-6 pb-28">
        {shows && shows.length > 0 ? (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {shows.map((show) => (
              <Link
                key={show.id}
                href={`/${show.slug}`}
                className="group relative overflow-hidden rounded-2xl border bg-white shadow-lg shadow-slate-900/5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-sky-400 no-underline hover:no-underline"
                style={show.siteSettings.heroBannerBoxBackgroundColor ? { borderColor: show.siteSettings.heroBannerBoxBackgroundColor } : {}}
              >
                {/* Background image */}
                {show.siteSettings.heroBackgroundImage ? (
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-45 transition-opacity duration-200 group-hover:opacity-55"
                    style={{
                      backgroundImage: `url('${getImageURL(show.siteSettings.heroBackgroundImage)}')`,
                    }}
                  />
                ) : null}

                {/* Brand color wash + light gradient to keep text readable while letting colors pop */}
                {show.siteSettings.heroBannerBoxBackgroundColor ? (
                  <div
                    className="absolute inset-0 opacity-20 mix-blend-multiply"
                    style={{ backgroundColor: show.siteSettings.heroBannerBoxBackgroundColor }}
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/20 to-transparent" />

                {/* Content */}
                <div className="relative flex min-h-[18rem] flex-col items-center justify-start p-7 pt-10 pb-6">
                  {show.isActive === false && (
                    <div className="absolute right-3 top-3 rounded-md bg-amber-600 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow">
                      Inactive
                    </div>
                  )}
                  <div className="rounded-xl bg-white/70 px-4 py-3 backdrop-blur-sm transition-colors duration-200 group-hover:bg-white/80">
                    <img
                      src={getImageURL(show.siteSettings.imageLogoDark || show.siteSettings.imageLogoLight || "/images/getSerious onDark.png")}
                      alt={show.name}
                      className="max-h-16 w-auto object-contain"
                    />
                  </div>

                  <h3 className="mt-5 text-lg font-semibold tracking-wide text-neutral-900">
                    {show.name}
                  </h3>

                  {/* CTA (keep underline for clarity) */}
                  <span className="mt-2 text-sm font-medium text-sky-700 underline underline-offset-4 decoration-1">
                    {show.siteSettings.homeTagline?.match(/submit/i)
                      ? show.siteSettings.homeTagline
                      : (show.siteSettings as any).submissionFormMainCTACopy || 'Submit your take'}
                  </span>

                  {(show.siteSettings.homeTaglineSecondary || show.description) ? (
                    <p className="mt-1 max-w-[90%] text-center text-xs text-gray-600 md:max-w-[80%]">
                      {show.siteSettings.homeTaglineSecondary || show.description}
                    </p>
                  ) : null}
                </div>

                {/* Footer bar for subtle brand color if provided */}
                {(show.siteSettings.heroBannerBoxBackgroundColor || show.siteSettings.heroBannerTextColor) && (
                  <div
                    className="relative h-1.5 w-full"
                    style={{
                      backgroundColor:
                        show.siteSettings.heroBannerBoxBackgroundColor || show.siteSettings.heroBannerTextColor,
                    }}
                  />
                )}
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-20 text-gray-600">No shows available.</div>
        )}
      </div>

      {/* SiriusXM logo bottom center */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 opacity-90">
        <img
          src={getImageURL("/images/siriusxm_black.png")}
          alt="SiriusXM"
          className="h-9 w-auto"
        />
      </div>
    </div>
  );
}
